import type { ICreateGroupParams } from "@/api/WebApiClient.ts";
import { getCurrentRouteWithoutBasename } from "@/app-utils/route.ts";
import type { TGridSelModelIdentifier } from "@/app-utils/selection-model.ts";
import { capitalizeFirstLetterOfAllWords, pluralizeWord, toTitleCase } from "@/app-utils/string.ts";
import type { BlastHit, InputTableQuerySpec, JobResult, SequenceFeatureTypes } from "@/graphql.ts";
import type { IJobState } from "@/redux/modules/job.ts";
import type { SaveViewParams } from "@/redux/modules/saved-view.ts";
import type { IIdentifiable, ITableData } from "@/redux/modules/table-data.ts";
import {
  BLAST_SUB_VIEW,
  ENTITY_TYPE,
  FEATURE_DISPLAY_NAME,
  SEQUENCE_TYPE,
  VIEW,
  getSequenceFeatureTypeDisplayName,
} from "@/settings/settings.ts";
import type { SaveDialogProps } from "@/ui-modules/core/save-dialog/SaveDialog.tsx";

export const SelectionType2String = {
  SelectedRows: "selected rows",
  AllRows: "all rows",
  CurrentView: "current view",
  SelectedRegion: "selected region",
  VisibleRegion: "visible region",
};

// the source set of rows
export enum SelectionType {
  SelectedRows = 1,
  AllRows = 2,
  CurrentView = 3,
  SelectedRegion = 4,
  VisibleRegion = 5,
}

export enum SaveDialogInputFields {
  FIELD_NAME = "saveAs",
  FIELD_DESCRIPTION = "description",
  FIELD_MAKEPRIVATE = "makePrivate",
  FIELD_SOURCE = "source",
  FIELD_DESTINATION = "destination",
  FIELD_UPDATE = "update",
  FIELD_TAGS = "tags",
}

export const FORM_GROUP = "saveViewGroup";

export const VIEW_ENTITY = "view";
export const GROUP_ENTITY = "group";

export type ISavableEntityParams = ICreateGroupParams | SaveViewParams;

export interface SourceSelectOption {
  selectionType: SelectionType;
  targetEntityType?: string; // e.g. in case of BLAST_HITS entity, the target can be different e.g. PROTEIN or TRANSCRIPT
  targetEntitySubTypeId?: number;
  table?: ITableData;
  targetRowIds?: TGridSelModelIdentifier[];
  multiTypesTarget?: boolean; // to warn the user if there are multiple options fitting the target
}

export interface TargetEntityOption {
  targetEntity: string;
  targetEntitySubTypeId?: number;
  targetRowIds: TGridSelModelIdentifier[];
  targetSelectedIds: TGridSelModelIdentifier[];
  multiTypesTarget?: boolean;
}

// when saving a view we need to provide specific viewLabel :
// * in the case of Table View or Summary View the option would show the entity type e.g. "Gene Summary" or "Protein Table"
// * in the case of BLAST View we show the actual tool, but since we don't have it at hand here
// * otherwise we just take the static view title from VIEW constant, like in the source drop down
export function getViewEntityLabel(props: SaveDialogProps, jobState?: IJobState): string {
  switch (props.viewType) {
    case VIEW.ENTITY_SUMMARY_VIEW.type:
      return toTitleCase(FEATURE_DISPLAY_NAME[props.entityType]);
    case VIEW.TABLE_VIEW.type:
      // eslint-disable-next-line no-case-declarations
      const table: ITableData = props.activeTables?.[0];
      if (jobState?.job?.params?.blast?.blastType) {
        switch (props.subViewType) {
          case BLAST_SUB_VIEW.HITS:
            return jobState.job.params.blast.blastType.toUpperCase() + " Hit";
          case BLAST_SUB_VIEW.HSPS:
            return jobState.job.params.blast.blastType.toUpperCase() + " HSP";
          default:
        }
      }
      return table && toTitleCase(getSequenceFeatureTypeDisplayName(table.tableQuerySpec));
    default:
      return null;
  }
}

export function getTypeName(entity: IIdentifiable, entityType: string, table: ITableData): string {
  return entity ? entityType : table?.tableQuerySpec.resultType;
}

export function createSavableEntityInputParams(
  values: Partial<FormData>,
  props: SaveDialogProps,
  viewId?: number,
): SaveViewParams {
  const payload: ISavableEntityParams = {
    project: values[SaveDialogInputFields.FIELD_DESTINATION],
    shared: !values[SaveDialogInputFields.FIELD_MAKEPRIVATE],
    description: values[SaveDialogInputFields.FIELD_DESCRIPTION],
    name: values[SaveDialogInputFields.FIELD_NAME]?.trim(),
    itemTagIds: values[SaveDialogInputFields.FIELD_TAGS],
  };

  const sourceSelection: SourceSelectOption = values[SaveDialogInputFields.FIELD_SOURCE];
  const selectionType: SelectionType = sourceSelection.selectionType;
  if (selectionType === SelectionType.CurrentView) {
    // Beware: most of this data is rewritten in saved-views.ts: saveView
    (payload as SaveViewParams).route = getCurrentRouteWithoutBasename();
    (payload as SaveViewParams).viewId = values[SaveDialogInputFields.FIELD_UPDATE] ? viewId : null;
    (payload as SaveViewParams).viewType = props.viewType;
    (payload as SaveViewParams).entityType = props.entityType;
    (payload as SaveViewParams).entityId = props.entity?.id as number;
    (payload as SaveViewParams).subViewType = props.subViewType;
    (payload as SaveViewParams).tableSpec = sourceSelection.table?.tableQuerySpec;
    (payload as SaveViewParams).viewEntityLabel = getViewEntityLabel(props);
  } else if (selectionType === SelectionType.AllRows || selectionType === SelectionType.SelectedRows) {
    // Create ICreateGroupParams from current state
    const typeName: string = getTypeName(props.entity, props.entityType, sourceSelection.table);
    payload.tableQuerySpec = {
      resultType: typeName,
      restrictions: {
        selection: {
          selectedResultIds: sourceSelection.targetRowIds as number[],
        },
      },
    };
    payload.targetType = typeName !== sourceSelection.targetEntityType ? sourceSelection.targetEntityType : undefined;
  }

  return payload;
}

export const getViewOptionLabel = (viewType: string) => {
  return VIEW[viewType].title;
};

// @ts-ignore
export const formatSourceOptionLabel = (
  option: SourceSelectOption,
  viewType?: string,
  sequenceFeatureTypes?: SequenceFeatureTypes,
  maxSelectedTableRows?: number,
) => {
  if (option.selectionType === SelectionType.CurrentView) {
    return getViewOptionLabel(viewType);
  }

  const targetTableQuerySpec: InputTableQuerySpec = {
    resultType: option.targetEntityType,
    resultSubTypeId: option.targetEntitySubTypeId,
    queryType: option.table?.tableQuerySpec.queryType,
  };
  const typeDisplayName: string = getSequenceFeatureTypeDisplayName(targetTableQuerySpec, sequenceFeatureTypes);
  const groupLabel = capitalizeFirstLetterOfAllWords(typeDisplayName);
  const itemsCount = option.targetRowIds.length;
  const itemsLabel = pluralizeWord(typeDisplayName, itemsCount);

  switch (option.selectionType) {
    case SelectionType.AllRows: {
      if (itemsCount === 1) {
        return `${groupLabel} Group - 1 ${itemsLabel}`;
      }
      const countLabel: string =
        itemsCount < maxSelectedTableRows ? `All ${itemsCount}` : `First ${maxSelectedTableRows}`;
      return `${groupLabel} Group - ${countLabel} ${itemsLabel}`;
    }
    case SelectionType.SelectedRows: {
      return `${groupLabel} Group - ${itemsCount} selected ${itemsLabel}`;
    }
  }
};

export const getSelectedItemCount = (table: ITableData, entity: IIdentifiable) => {
  return !entity && table ? table.selectionModel.selectedIds.length : 0;
};

export const getDefaultSourceOption = (
  optionList: SourceSelectOption[],
  viewType?: string,
  entity?: IIdentifiable,
  tables?: ITableData[],
  isGenomeBrowserRangeSelected?: boolean,
) => {
  if (
    // in the content browser and nothing selected...
    (viewType &&
      viewType === VIEW.CONTENT_BROWSER_VIEW.type &&
      tables &&
      getSelectedItemCount(tables[0], entity) === 0) ||
    // ... or in the genome viewer and a range is selected...
    (viewType && viewType === VIEW.GENOME_VIEW.type && isGenomeBrowserRangeSelected)
  ) {
    // ...default on the view...
    return (
      optionList.find((source: SourceSelectOption) => source.selectionType === SelectionType.CurrentView) ||
      (optionList.length > 0 && optionList[0])
    );
  } else {
    // ...otherwise on the selected rows, then all rows
    return (
      optionList.find((source: SourceSelectOption) => source.selectionType === SelectionType.SelectedRows) ||
      optionList.find((source: SourceSelectOption) => source.selectionType === SelectionType.AllRows) ||
      (optionList.length > 0 && optionList[0])
    );
  }
};

export const getBlastHitsIds = (
  table: ITableData,
  sequenceType: string,
  selectedOnly: boolean,
  jobState: IJobState,
) => {
  const jobResult: JobResult = jobState ? jobState.jobResults[table.tableQuerySpec.id] : null;
  const blastHits: BlastHit[] = jobResult?.blastHits.filter((blastHit: BlastHit) => {
    return (
      blastHit.target?.type.name === sequenceType &&
      (!selectedOnly || table.selectionModel.selectedIds.includes(blastHit.id))
    );
  });
  return blastHits ? blastHits.map((blastHit) => blastHit.id) : [];
};

export const getSourceOptions = (
  targetEntityOptions: TargetEntityOption[],
  table?: ITableData,
  viewType?: string,
  maxSelectedTableRows?: number,
) => {
  const optionList: SourceSelectOption[] = [];
  // translate e.g. geneMutation into 'gene' or 'variant', depending on the parent entity
  targetEntityOptions.forEach((targetEntityOption: TargetEntityOption) => {
    if (targetEntityOption.targetSelectedIds.length) {
      optionList.push({
        selectionType: SelectionType.SelectedRows,
        targetEntityType: targetEntityOption.targetEntity,
        table: table,
        targetRowIds: targetEntityOption.targetSelectedIds,
        multiTypesTarget: targetEntityOption.multiTypesTarget,
      });
    }

    if (targetEntityOption.targetRowIds.length && viewType !== VIEW.GENOME_VIEW.type) {
      optionList.push({
        selectionType: SelectionType.AllRows,
        targetEntityType: targetEntityOption.targetEntity,
        table: table,
        targetRowIds:
          targetEntityOption.targetRowIds.length > maxSelectedTableRows
            ? targetEntityOption.targetRowIds.slice(0, maxSelectedTableRows)
            : targetEntityOption.targetRowIds,
        multiTypesTarget: targetEntityOption.multiTypesTarget,
      });
    }
  });

  return optionList;
};

export const getSaveAsOptionParameters = (
  entityType: string,
  table: ITableData,
  entity: IIdentifiable,
  jobState?: IJobState,
): TargetEntityOption[] => {
  const targetIds: TGridSelModelIdentifier[] = !entity && table ? table.rowIds : [entity.id];
  const selectedTargetIds: TGridSelModelIdentifier[] = !entity && table ? table.selectionModel.selectedIds : [];

  switch (entityType) {
    case ENTITY_TYPE.BLAST_HIT:
      return [
        {
          targetEntity: ENTITY_TYPE.PROTEIN,
          targetRowIds: getBlastHitsIds(table, SEQUENCE_TYPE.PROTEIN, false, jobState),
          targetSelectedIds: getBlastHitsIds(table, SEQUENCE_TYPE.PROTEIN, true, jobState),
        },
        {
          targetEntity: ENTITY_TYPE.TRANSCRIPT,
          targetRowIds: getBlastHitsIds(table, SEQUENCE_TYPE.TRANSCRIPT, false, jobState),
          targetSelectedIds: getBlastHitsIds(table, SEQUENCE_TYPE.TRANSCRIPT, true, jobState),
        },
      ];
    case ENTITY_TYPE.GENE_MUTATION:
      // can be two perspectives: from gene or from variant
      return [
        {
          targetEntity: table.tableQuerySpec.queryType === ENTITY_TYPE.GENE ? ENTITY_TYPE.MUTATION : ENTITY_TYPE.GENE,
          targetRowIds: targetIds,
          targetSelectedIds: selectedTargetIds,
        },
      ];
    case ENTITY_TYPE.PROTEIN_MUTATION:
      // can be two perspectives: from protein or from variant
      return [
        {
          targetEntity:
            table.tableQuerySpec.queryType === ENTITY_TYPE.PROTEIN ? ENTITY_TYPE.MUTATION : ENTITY_TYPE.PROTEIN,
          targetRowIds: targetIds,
          targetSelectedIds: selectedTargetIds,
        },
      ];
    case ENTITY_TYPE.VARIANT:
      // can be two perspectives: from protein or from variant
      return [
        {
          targetEntity: ENTITY_TYPE.MUTATION,
          targetRowIds: targetIds,
          targetSelectedIds: selectedTargetIds,
        },
      ];
    default:
      return [
        {
          targetEntity: entityType,
          targetEntitySubTypeId: !entity ? table?.tableQuerySpec.resultSubTypeId : null,
          targetRowIds: targetIds,
          targetSelectedIds: selectedTargetIds,
        },
      ];
  }
};
